/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import { RefreshCw, Send, Eye, EyeOff, Clock, ExternalLink, Mail, MessageSquare } from "lucide-react";
import { fmtD } from "../lib/date";

const Kpi = ({ label, value, tint, tid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={tid}>
    <p className="text-xs text-slate-400">{label}</p>
    <p className="text-2xl font-bold mt-1" style={{ color: tint || "#0f172a" }}>{value}</p>
  </div>
);

export default function CRMUnsignedContracts() {
  const { lang, user } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const isMgr = ["admin", "ceo", "sales_vp", "sales_manager", "marketing", "eng_manager"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/crm/contracts/unsigned"); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resend = async (r) => {
    const channel = r.hasEmail ? "email" : r.hasPhone ? "sms" : null;
    if (!channel) { toast.error(zh ? "客户资料无邮箱 / 手机号，无法重发；请先在客户详情补全" : "No email / phone on file"); return; }
    const via = channel === "email" ? (zh ? "邮件" : "email") : (zh ? "短信" : "SMS");
    if (!window.confirm(zh ? `确认通过${via}向 ${r.clientName || "业主"} 重发签署链接？（会实发）` : `Resend signing link to ${r.clientName || "owner"} via ${via}?`)) return;
    setBusy(r.leadId);
    try {
      const { data } = await api.post(`/crm/leads/${r.leadId}/contract/send-link`, { channel });
      if (data.ok) toast.success(zh ? `已通过${via}重发签署链接` : `Resent via ${via}`);
      else toast.error((data.errors || []).join("；") || (zh ? "未发送" : "Not sent"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const rows = (data?.rows || []).filter((r) => filter === "all" || (filter === "unsent" && !r.sentAt) || (filter === "unviewed" && r.sentAt && !r.viewedAt) || (filter === "over7" && (r.daysSinceSent || 0) >= 7));
  const s = data?.summary || {};
  const FILTERS = [["all", zh ? "全部" : "All", s.total], ["over7", zh ? "已发 ≥ 7 天" : "Sent ≥ 7d", s.over7], ["unviewed", zh ? "已发未查看" : "Unviewed", s.unviewed], ["unsent", zh ? "仅生成链接未发送" : "Link only", s.unsent]];

  return (
    <div className="space-y-5" data-testid="unsigned-contracts-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="sr-only">{zh ? "合同签署提醒" : "Contract Signing Reminders"}</h1>
        <span className="text-sm text-slate-500">{zh ? (isMgr ? "全部业主合同 · 已发送 / 已分享但未签署" : "我的客户 · 已发送 / 已分享但未签署") : "Sent / shared but not signed"}</span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={load} data-testid="unsigned-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />{zh ? "刷新" : "Refresh"}</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={zh ? "未签署合同" : "Unsigned"} value={s.total ?? "–"} tid="unsigned-kpi-total" />
        <Kpi label={zh ? "已发送 ≥ 7 天" : "Sent ≥ 7 days"} value={s.over7 ?? "–"} tint="#dc2626" tid="unsigned-kpi-over7" />
        <Kpi label={zh ? "已发送未查看" : "Unviewed"} value={s.unviewed ?? "–"} tint="#d97706" tid="unsigned-kpi-unviewed" />
        <Kpi label={zh ? "仅生成链接未发送" : "Link only"} value={s.unsent ?? "–"} tint="#64748b" tid="unsigned-kpi-unsent" />
      </div>
      <div className="flex gap-2 flex-wrap" data-testid="unsigned-filters">
        {FILTERS.map(([k, l, n]) => (
          <button key={k} data-testid={`unsigned-filter-${k}`} data-on={filter === k ? 1 : 0} onClick={() => setFilter(k)} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`)}>{l}{n != null && <span className="ml-1 opacity-70">({n})</span>}</button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <ResponsiveTable className="w-full text-sm" data-testid="unsigned-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5">{zh ? "客户 / 地址" : "Client / address"}</th>
              <th className="text-left px-3 py-2.5">{zh ? "合同编号 · 总价" : "Contract · total"}</th>
              <th className="text-left px-3 py-2.5">{zh ? "发送" : "Sent"}</th>
              <th className="text-left px-3 py-2.5">{zh ? "查看 / 催看" : "Viewed / nudged"}</th>
              {isMgr && <th className="text-left px-3 py-2.5">{zh ? "负责销售" : "Sales"}</th>}
              <th className="text-right px-4 py-2.5">{zh ? "操作" : "Action"}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && rows.length === 0 && (
              <tr><td colSpan={isMgr ? 6 : 5} className="px-4 py-10 text-center text-slate-400" data-testid="unsigned-empty">{zh ? "没有待签署的合同" : "No unsigned contracts"}</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.leadId} data-testid={`unsigned-row-${r.leadId}`} className="hover:bg-slate-50/60">
                <td className="px-4 py-3">
                  <button className="font-semibold text-slate-900 hover:text-brand-600 text-left" data-testid={`unsigned-open-${r.leadId}`} onClick={() => nav(`/crm/leads/${r.leadId}`)}>{r.clientName || r.leadId}</button>
                  <p className="text-xs text-slate-500 truncate max-w-[340px]">{r.address}</p>
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <p className="font-mono text-xs text-slate-700">{r.contractNo || "—"}</p>
                  <p className="text-sm font-semibold text-slate-900" data-testid={`unsigned-total-${r.leadId}`}>{r.totalPrice || (zh ? "待补全" : "pending")}</p>
                </td>
                <td className="px-3 py-3 whitespace-nowrap" data-testid={`unsigned-sent-${r.leadId}`}>
                  {r.sentAt ? (
                    <>
                      <p className={`text-sm font-bold ${(r.daysSinceSent || 0) >= 7 ? "text-red-600" : (r.daysSinceSent || 0) >= 3 ? "text-amber-600" : "text-slate-800"}`}><Clock className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />{zh ? `已发送 ${r.daysSinceSent} 天` : `${r.daysSinceSent}d ago`}</p>
                      <p className="text-[11px] text-slate-400">{fmtD(r.sentAt)}{r.lastSentBy ? ` · ${r.lastSentBy}` : ""}</p>
                      {r.signDeadline && <p className="text-[11px] text-amber-600">{zh ? "优惠截止" : "Offer until"} {r.signDeadline}</p>}
                    </>
                  ) : <span className="text-xs text-slate-400">{zh ? "仅生成链接，未发送" : "Link created, not sent"}</span>}
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-xs" data-testid={`unsigned-viewed-${r.leadId}`}>
                  {r.viewedAt ? <span className="text-emerald-700 inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{zh ? "已查看" : "Viewed"} {fmtD(r.lastViewedAt || r.viewedAt)}</span>
                    : r.sentAt ? <span className="text-amber-600 inline-flex items-center gap-1"><EyeOff className="h-3.5 w-3.5" />{zh ? "未查看" : "Not viewed"}</span> : <span className="text-slate-300">—</span>}
                  {r.lastNudgeAt && <p className="text-[11px] text-slate-400 mt-0.5">{zh ? "最近催看" : "Nudged"} {fmtD(r.lastNudgeAt)}</p>}
                </td>
                {isMgr && <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`unsigned-sales-${r.leadId}`}>{r.assignedSales || "—"}</td>}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <span className="inline-flex items-center gap-1 mr-2 text-slate-400" title={zh ? "可用通道" : "Channels"}>
                    <Mail className={`h-3.5 w-3.5 ${r.hasEmail ? "text-emerald-600" : "text-slate-200"}`} /><MessageSquare className={`h-3.5 w-3.5 ${r.hasPhone ? "text-emerald-600" : "text-slate-200"}`} />
                  </span>
                  <Button size="sm" data-testid={`unsigned-resend-${r.leadId}`} disabled={busy === r.leadId || (!r.hasEmail && !r.hasPhone)} onClick={() => resend(r)} title={!r.hasEmail && !r.hasPhone ? (zh ? "无邮箱 / 手机号" : "No email / phone") : ""} className="bg-brand-600 hover:bg-brand-700 text-white">
                    <Send className="h-3.5 w-3.5 mr-1" />{zh ? "重发签署链接" : "Resend link"}
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`unsigned-lead-${r.leadId}`} onClick={() => nav(`/crm/leads/${r.leadId}`)} title={zh ? "打开客户" : "Open lead"}><ExternalLink className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
}
